/**
 * VoiceRecorderBar
 *
 * Presentational component for the "recording" and "transcribing"
 * states of a voice recorder inside a chat input. Pair with
 * `useVoiceRecorder` — the hook drives the state, this component
 * renders the UI.
 *
 *   state="recording"     → pulsing red dot, animated waveform, red stop button
 *   state="transcribing"  → spinner + "Transkribiere…" label, no stop button
 *
 * Kept intentionally tiny — the container (textarea, widget) decides
 * whether it replaces the idle input or sits next to it.
 */

import { Loader2, Square } from 'lucide-react';

export type VoiceRecorderBarState = 'recording' | 'transcribing';

type VoiceRecorderBarProps = {
  state: VoiceRecorderBarState;
  onStop: () => void;
  className?: string;
};

export function VoiceRecorderBar({ state, onStop, className = '' }: VoiceRecorderBarProps) {
  return (
    <div
      className={
        'flex items-center gap-3 rounded-2xl border border-primary/40 bg-primary/5 px-4 h-11 ' +
        className
      }
    >
      <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />
      <span className="text-sm text-primary font-medium flex-1 truncate">
        {state === 'recording' ? 'Aufnahme läuft…' : 'Transkribiere…'}
      </span>
      {state === 'recording' ? (
        <>
          <div className="flex items-end gap-0.5 h-5">
            {[8, 16, 12, 20, 10].map((h, i) => (
              <div
                key={i}
                className="w-[3px] rounded-full bg-primary animate-bounce"
                style={{ height: `${h}px`, animationDelay: `${i * 80}ms` }}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={onStop}
            aria-label="Aufnahme stoppen"
            className="h-7 w-7 p-0 bg-red-500 hover:bg-red-600 text-white rounded-lg shrink-0 flex items-center justify-center transition-colors"
          >
            <Square className="w-3.5 h-3.5 fill-current" />
          </button>
        </>
      ) : (
        <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" />
      )}
    </div>
  );
}
